import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import {
  ActionBar,
  Button,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Table,
  TableWrap,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface AssignedUser {
  _id: string;
  twitchId: string;
  username: string;
  displayName: string;
  discordId?: string;
  discordUsername?: string;
  discordDisplayName?: string;
  oracleClaimsLeft: number;
  lastSeenAt?: string;
}

const OracleAssignedPage: React.FC = () => {
  const [users, setUsers] = useState<AssignedUser[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState<AssignedUser | null>(null);
  const [claims, setClaims] = useState('0');
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/users/oracle-assigned', {
      params: { search, filter },
    });
    setUsers(data.users || []);
  };

  useEffect(() => {
    load();
  }, []);

  const saveClaims = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    await api.put(`/users/${editing._id}`, {
      oracleClaimsLeft: parseInt(claims, 10) || 0,
    });
    setEditing(null);
    setToast('Cartas asignadas actualizadas');
    load();
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Oráculos asignados</Title>
          <Subtitle>
            Cantidad de cartas/oráculos disponibles por usuario. Editá a mano; al publicar se descuenta (salvo regalo).
          </Subtitle>
        </PageHeaderMain>
      </PageHeader>

      {toast && <Toast onClick={() => setToast('')}>{toast}</Toast>}

      <Surface>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Twitch o Discord…"
              style={{ maxWidth: 280 }}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            />
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 180 }}>
              <option value="all">Todos</option>
              <option value="withClaims">Con cartas &gt; 0</option>
              <option value="linked">Discord vinculado</option>
              <option value="unlinked">Sin Discord</option>
            </Select>
            <Button $size="sm" type="button" onClick={load}>Buscar</Button>
            <Muted style={{ margin: 0 }}>{users.length} resultados</Muted>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Twitch</th>
                  <th>Display</th>
                  <th>Discord</th>
                  <th style={{ width: '12%' }}>Cartas</th>
                  <th style={{ width: '14%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.username}</td>
                    <td>{u.displayName}</td>
                    <td>
                      {u.discordId
                        ? (u.discordDisplayName || u.discordUsername || u.discordId)
                        : '—'}
                    </td>
                    <td>{u.oracleClaimsLeft || 0}</td>
                    <td style={{ textAlign: 'right' }}>
                      <ActionBar>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          type="button"
                          onClick={() => {
                            setEditing(u);
                            setClaims(String(u.oracleClaimsLeft || 0));
                          }}
                        >
                          Editar
                        </Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>
                      Sin usuarios
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={!!editing}
        title={editing ? `Cartas · ${editing.displayName}` : 'Editar'}
        onClose={() => setEditing(null)}
        footer={editing ? (
          <>
            <Button $variant="ghost" type="button" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button type="submit" form="claims-form">Guardar</Button>
          </>
        ) : undefined}
      >
        {editing && (
          <form id="claims-form" onSubmit={saveClaims}>
            <FormRow>
              <Label>Oráculos / cartas disponibles</Label>
              <Input type="number" min={0} value={claims} onChange={(e) => setClaims(e.target.value)} />
            </FormRow>
          </form>
        )}
      </Modal>
    </Page>
  );
};

export default OracleAssignedPage;
